import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  getDiscussion, postDiscussion, editDiscussion, deleteDiscussion, reactDiscussion,
  readDiscussionMentions, uploadDiscussionAttachment, discussionAttachmentUrl, getTeam, getMe,
  type DiscussionMessage, type DiscussionAttachment, type TeamMember,
} from '../lib/api.js';
import { Card, Spinner } from './ui.js';

/**
 * Deal discussion — one flat, chronological channel per deal where any message can carry a
 * thread of replies. Replaces the old flat notes log.
 *
 * Mentions are resolved from the message TEXT at send time (every "@Full Name" that matches an
 * active teammate) rather than tracked as the user types, so editing the text afterwards can't
 * leave a phantom mention behind and a hand-typed name still notifies.
 *
 * Every write is applied to the cache FIRST and reconciled with the server's reply — never by
 * refetching the channel. Posting used to cost two serialised round-trips (POST, then a full
 * re-read) before anything appeared on screen, which read as a ~10s stall; now the message is
 * on screen in the same frame you press Enter.
 */

const REACTIONS = ['👍', '🎉', '👀', '🔥', '❓', '⚠️'] as const;

/** A message plus client-only state: `pending` while its write is in flight. */
type UiMessage = DiscussionMessage & { pending?: boolean; replies: UiMessage[] };
interface Channel { messages: UiMessage[] }
const keyFor = (dealId: string) => ['discussion', dealId] as const;

/* ------------------------------------------------------------ cache edits -- */
/*
 * The channel is a two-level tree, so every edit recurses one level into `replies`. These are
 * pure and immutable: React Query hands them the previous cache value and stores the result.
 */

function patchIn(list: UiMessage[], id: string, patch: (m: UiMessage) => UiMessage): UiMessage[] {
  return list.map((m) => (m.id === id ? patch(m) : { ...m, replies: patchIn(m.replies, id, patch) }));
}
function removeIn(list: UiMessage[], id: string): UiMessage[] {
  return list.filter((m) => m.id !== id).map((m) => ({ ...m, replies: removeIn(m.replies, id) }));
}
function insertIn(list: UiMessage[], msg: UiMessage): UiMessage[] {
  if (!msg.parentId) return [...list, msg];
  if (list.some((m) => m.id === msg.parentId)) {
    return list.map((m) => (m.id === msg.parentId ? { ...m, replies: [...m.replies, msg] } : m));
  }
  return [...list, msg]; // parent isn't in view — show it rather than swallow it
}
function has(list: UiMessage[], id: string): boolean {
  return list.some((m) => m.id === id || has(m.replies, id));
}
/**
 * Swap a message for the server's authoritative copy. Falls back to inserting when the target
 * has gone — a background poll can land between the optimistic write and the response, which
 * would otherwise drop the message until the next poll.
 */
function replaceOrInsert(list: UiMessage[], id: string, real: DiscussionMessage): UiMessage[] {
  const next = real as UiMessage;
  return has(list, id) ? patchIn(list, id, (m) => ({ ...next, replies: m.replies })) : insertIn(list, next);
}

function toggleReaction(m: UiMessage, emoji: string, meName: string): UiMessage {
  const existing = m.reactions.find((r) => r.emoji === emoji);
  if (existing?.mine) {
    const reactions = existing.count <= 1
      ? m.reactions.filter((r) => r.emoji !== emoji)
      : m.reactions.map((r) => (r.emoji === emoji ? { ...r, count: r.count - 1, mine: false, who: r.who.filter((w) => w !== meName) } : r));
    return { ...m, reactions };
  }
  if (existing) {
    return { ...m, reactions: m.reactions.map((r) => (r.emoji === emoji ? { ...r, count: r.count + 1, mine: true, who: [...r.who, meName] } : r)) };
  }
  return { ...m, reactions: [...m.reactions, { emoji, count: 1, mine: true, who: [meName] }] };
}

/** Snapshot → mutate → roll back on failure, the shape every write here uses. */
function editCache(qc: QueryClient, dealId: string, fn: (list: UiMessage[]) => UiMessage[]) {
  const key = keyFor(dealId);
  const prev = qc.getQueryData<Channel>(key);
  qc.setQueryData<Channel>(key, { messages: fn(prev?.messages ?? []) });
  return prev;
}
const restoreCache = (qc: QueryClient, dealId: string, prev: Channel | undefined) => {
  if (prev) qc.setQueryData<Channel>(keyFor(dealId), prev);
};

/* ---------------------------------------------------------------- display -- */

/** Deterministic avatar tint per member, so a person looks the same everywhere in the thread. */
const AVATAR_TINTS = [
  'bg-teal-50 text-teal-d', 'bg-gold-50 text-gold', 'bg-green-50 text-green',
  'bg-purple-50 text-purple', 'bg-amber-50 text-amber', 'bg-navy-50 text-navy',
];
function tintFor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_TINTS[h % AVATAR_TINTS.length]!;
}

function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  if (mins < 60 * 24 * 7) return `${Math.round(mins / (60 * 24))}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
const fullTime = (iso: string) => new Date(iso).toLocaleString();
const dayKey = (iso: string) => new Date(iso).toDateString();
function dayLabel(iso: string): string {
  const d = new Date(iso); const today = new Date();
  const yday = new Date(today); yday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}
const fileSize = (n: number | null) => (n == null ? '' : n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / (1024 * 1024)).toFixed(1)} MB`);
const escRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Every "@Name" in `text` that matches an active teammate (full name or first name). */
function mentionIdsIn(text: string, team: TeamMember[]): string[] {
  const out = new Set<string>();
  for (const m of team) {
    const first = m.name.split(/\s+/)[0] ?? '';
    const forms = [m.name, ...(first && first !== m.name ? [first] : [])];
    for (const f of forms) {
      if (new RegExp(`@${escRe(f)}(?![\\p{L}])`, 'u').test(text)) { out.add(m.id); break; }
    }
  }
  return [...out];
}

/** Highlight @mentions, styling the signed-in user's own mention more strongly. */
function renderBody(text: string, team: TeamMember[], meName: string): ReactNode {
  const forms = team.flatMap((m) => { const f = m.name.split(/\s+/)[0] ?? ''; return f && f !== m.name ? [m.name, f] : [m.name]; });
  const uniq = [...new Set(forms)].filter(Boolean).sort((a, b) => b.length - a.length);
  if (!uniq.length) return text;
  const re = new RegExp(`@(${uniq.map(escRe).join('|')})(?![\\p{L}])`, 'gu');
  const out: ReactNode[] = [];
  let last = 0; let m: RegExpExecArray | null; let key = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const isMe = meName.toLowerCase().startsWith((m[1] ?? '').toLowerCase());
    out.push(
      <span key={key++} className={`rounded px-1 font-semibold ${isMe ? 'bg-gold-50 text-gold' : 'bg-teal-50 text-teal-d'}`}>@{m[1]}</span>,
    );
    last = re.lastIndex;
  }
  out.push(text.slice(last));
  return out;
}

function Avatar({ name, initials, size = 28 }: { name: string; initials: string; size?: number }) {
  return (
    <div title={name} style={{ width: size, height: size, fontSize: size * 0.38 }}
      className={`flex-none rounded-full grid place-items-center font-bold ${tintFor(name)}`}>
      {(initials || name.slice(0, 1)).slice(0, 2).toUpperCase()}
    </div>
  );
}

/* --------------------------------------------------------------- composer -- */

export interface Draft { body: string; mentions: string[]; attachmentIds: string[] }

interface ComposerProps {
  dealId: string;
  team: TeamMember[];
  placeholder: string;
  initial?: string;
  submitLabel: string;
  autoFocus?: boolean;
  allowAttachments?: boolean;
  onSubmit: (v: Draft) => void;
  onCancel?: () => void;
}

/**
 * Message box with @-autocomplete. Enter sends, Shift+Enter makes a newline — except while the
 * mention menu is open, where Enter/Tab picks the highlighted teammate instead. Clears on send
 * without waiting for the server; the caller is responsible for showing the message optimistically
 * and for handing the text back if the write fails.
 */
function Composer({ dealId, team, placeholder, initial = '', submitLabel, autoFocus, allowAttachments, onSubmit, onCancel }: ComposerProps) {
  const [text, setText] = useState(initial);
  const [sel, setSel] = useState(0);
  const [files, setFiles] = useState<DiscussionAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (autoFocus) ref.current?.focus(); }, [autoFocus]);
  // Grow with content instead of scrolling inside a 2-line box.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [text]);

  // A trailing "@query" (up to the caret) opens the menu; picking replaces just that token.
  const query = useMemo(() => {
    const m = text.match(/@([\p{L}][\p{L}\s]*)?$/u);
    return m ? (m[1] ?? '').trimStart().toLowerCase() : null;
  }, [text]);
  const suggestions = useMemo(
    () => (query === null ? [] : team.filter((m) => m.name.toLowerCase().includes(query)).slice(0, 6)),
    [query, team],
  );
  useEffect(() => { setSel(0); }, [query]);

  const pick = (name: string) => {
    setText((t) => t.replace(/@([\p{L}][\p{L}\s]*)?$/u, `@${name} `));
    ref.current?.focus();
  };

  const send = () => {
    const body = text.trim();
    if ((!body && !files.length) || uploading) return;
    onSubmit({ body, mentions: mentionIdsIn(body, team), attachmentIds: files.map((f) => f.id) });
    setText(''); setFiles([]); setErr(null);
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => (s + 1) % suggestions.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => (s - 1 + suggestions.length) % suggestions.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(suggestions[sel]!.name); return; }
      if (e.key === 'Escape') { e.preventDefault(); setText((t) => `${t} `); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); return; }
    if (e.key === 'Escape' && onCancel) { e.preventDefault(); onCancel(); }
  };

  const addFiles = async (list: FileList | null) => {
    if (!list?.length) return;
    setUploading(true); setErr(null);
    try {
      for (const f of Array.from(list).slice(0, 10 - files.length)) {
        const { attachment } = await uploadDiscussionAttachment(dealId, f);
        setFiles((prev) => [...prev, attachment]);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div>
      <div className="relative">
        <textarea
          ref={ref}
          value={text}
          rows={2}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          onPaste={(e) => { if (allowAttachments && e.clipboardData.files.length) { e.preventDefault(); void addFiles(e.clipboardData.files); } }}
          placeholder={placeholder}
          className="w-full resize-none px-3 py-2 border border-line rounded-lg text-[13px] leading-relaxed outline-none focus:border-teal focus:ring-2 focus:ring-teal/15"
        />
        {suggestions.length > 0 && (
          <div className="absolute z-20 left-0 right-0 bottom-full mb-1 bg-card border border-line rounded-lg shadow-sh2 overflow-hidden">
            {suggestions.map((m, i) => (
              <button key={m.id} type="button" onMouseDown={(e) => { e.preventDefault(); pick(m.name); }}
                onMouseEnter={() => setSel(i)}
                className={`w-full text-left px-3 py-1.5 text-[12.5px] flex items-center gap-2 ${i === sel ? 'bg-teal-50' : 'hover:bg-canvas'}`}>
                <Avatar name={m.name} initials={m.initials} size={20} />
                <span className="font-semibold text-navy">{m.name}</span>
                <span className="text-[10.5px] text-muted-2 ml-auto capitalize">{m.role}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {files.map((f) => (
            <span key={f.id} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md bg-navy-50 text-navy">
              📎 {f.name} <span className="text-muted-2">{fileSize(f.size)}</span>
              <button type="button" onClick={() => setFiles((p) => p.filter((x) => x.id !== f.id))} className="text-muted-2 hover:text-red" aria-label={`Remove ${f.name}`}>×</button>
            </span>
          ))}
        </div>
      )}
      {err && <div className="mt-1.5 text-[11px] text-red">{err}</div>}

      <div className="flex items-center gap-2 mt-2">
        {allowAttachments && (
          <>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => void addFiles(e.target.files)} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || files.length >= 10}
              className="text-[11.5px] px-2 py-1 rounded-md border border-line text-muted hover:text-navy hover:border-navy-50 disabled:opacity-50">
              {uploading ? <Spinner size={12} /> : '📎'} Attach
            </button>
          </>
        )}
        <span className="text-[10.5px] text-muted-2">Type <b>@</b> to mention · Enter sends, Shift+Enter for a new line</span>
        <div className="ml-auto flex items-center gap-2">
          {onCancel && <button type="button" onClick={onCancel} className="text-[12px] px-2.5 py-1.5 rounded-lg text-muted hover:text-navy">Cancel</button>}
          <button type="button" onClick={send} disabled={uploading || (!text.trim() && !files.length)}
            className="px-3.5 py-1.5 rounded-lg text-[12.5px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50">
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- message -- */

interface MessageProps {
  m: UiMessage;
  dealId: string;
  team: TeamMember[];
  meName: string;
  isReply?: boolean;
  replyingTo: string | null;
  setReplyingTo: (id: string | null) => void;
  editing: string | null;
  setEditing: (id: string | null) => void;
  onReply: (v: Draft, parentId: string) => void;
}

function Message({ m, dealId, team, meName, isReply, replyingTo, setReplyingTo, editing, setEditing, onReply }: MessageProps) {
  const qc = useQueryClient();
  const [showReplies, setShowReplies] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Each write paints the cache immediately, then swaps in the server's copy. `context` carries
  // the pre-write snapshot so a failure puts the channel back exactly as it was.
  const react = useMutation({
    mutationFn: (emoji: string) => reactDiscussion(m.id, emoji),
    onMutate: (emoji) => ({ prev: editCache(qc, dealId, (l) => patchIn(l, m.id, (x) => toggleReaction(x, emoji, meName))) }),
    onError: (_e, _v, ctx) => restoreCache(qc, dealId, ctx?.prev),
    onSuccess: (d) => editCache(qc, dealId, (l) => replaceOrInsert(l, m.id, d.message)),
  });
  const edit = useMutation({
    mutationFn: (v: { body: string; mentions: string[] }) => editDiscussion(m.id, v),
    onMutate: (v) => {
      setEditing(null);
      return { prev: editCache(qc, dealId, (l) => patchIn(l, m.id, (x) => ({ ...x, body: v.body, editedAt: new Date().toISOString(), pending: true }))) };
    },
    onError: (_e, _v, ctx) => restoreCache(qc, dealId, ctx?.prev),
    onSuccess: (d) => editCache(qc, dealId, (l) => replaceOrInsert(l, m.id, d.message)),
  });
  const del = useMutation({
    mutationFn: () => deleteDiscussion(m.id),
    onMutate: () => ({
      prev: editCache(qc, dealId, (l) => patchIn(l, m.id, (x) => ({
        ...x, deleted: true, body: 'This message was deleted.', mentions: [], reactions: [], attachments: [], pending: true,
      }))),
    }),
    onError: (_e, _v, ctx) => restoreCache(qc, dealId, ctx?.prev),
    onSuccess: (d) => editCache(qc, dealId, (l) => replaceOrInsert(l, m.id, d.message)),
  });

  if (editing === m.id) {
    return (
      <div className="flex gap-2.5 py-2">
        <Avatar name={m.author.name} initials={m.author.initials} size={isReply ? 24 : 28} />
        <div className="flex-1 min-w-0">
          <Composer dealId={dealId} team={team} initial={m.body} placeholder="Edit your message…" submitLabel="Save" autoFocus
            onSubmit={({ body, mentions }) => edit.mutate({ body, mentions })}
            onCancel={() => setEditing(null)} />
        </div>
      </div>
    );
  }

  return (
    <div className={`group flex gap-2.5 py-2 transition-opacity ${m.pending ? 'opacity-60' : ''}`}>
      <Avatar name={m.author.name} initials={m.author.initials} size={isReply ? 24 : 28} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <b className="text-navy text-[12.5px]">{m.author.name}</b>
          <span className="text-[10.5px] text-muted-2" title={fullTime(m.createdAt)}>
            {m.pending ? 'sending…' : relTime(m.createdAt)}
          </span>
          {m.editedAt && !m.pending && <span className="text-[10.5px] text-muted-2" title={`Edited ${fullTime(m.editedAt)}`}>(edited)</span>}

          {/* Hover actions — kept out of the flow so they never shift the text. */}
          {!m.deleted && !m.pending && (
            <span className="ml-auto flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
              <span className="relative">
                <button onClick={() => setPickerOpen((o) => !o)} title="Add reaction"
                  className="text-[11px] px-1.5 py-0.5 rounded border border-line text-muted hover:text-navy">😊</button>
                {pickerOpen && (
                  <span className="absolute z-20 right-0 mt-1 flex gap-0.5 bg-card border border-line rounded-lg shadow-sh2 p-1">
                    {REACTIONS.map((e) => (
                      <button key={e} onClick={() => { setPickerOpen(false); react.mutate(e); }}
                        className="px-1.5 py-0.5 rounded hover:bg-canvas text-[14px]">{e}</button>
                    ))}
                  </span>
                )}
              </span>
              {!isReply && (
                <button onClick={() => setReplyingTo(replyingTo === m.id ? null : m.id)}
                  className="text-[11px] px-1.5 py-0.5 rounded border border-line text-muted hover:text-navy">Reply</button>
              )}
              {m.canEdit && m.mine && (
                <button onClick={() => setEditing(m.id)} className="text-[11px] px-1.5 py-0.5 rounded border border-line text-muted hover:text-navy">Edit</button>
              )}
              {m.canEdit && (
                <button onClick={() => { if (confirm('Delete this message?')) del.mutate(); }}
                  className="text-[11px] px-1.5 py-0.5 rounded border border-line text-muted hover:text-red">Delete</button>
              )}
            </span>
          )}
        </div>

        <div className={`text-[12.5px] whitespace-pre-wrap break-words ${m.deleted ? 'text-muted-2 italic' : 'text-ink'}`}>
          {m.deleted ? m.body : renderBody(m.body, team, meName)}
        </div>

        {m.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-1.5">
            {m.attachments.map((a) => (
              (a.type ?? '').startsWith('image/') ? (
                <a key={a.id} href={discussionAttachmentUrl(dealId, a.id)} target="_blank" rel="noreferrer" title={a.name}>
                  <img src={discussionAttachmentUrl(dealId, a.id)} alt={a.name}
                    className="max-h-40 rounded-lg border border-line object-cover" />
                </a>
              ) : (
                <a key={a.id} href={discussionAttachmentUrl(dealId, a.id)} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11.5px] px-2 py-1 rounded-md border border-line text-navy hover:border-teal">
                  📎 {a.name} <span className="text-muted-2">{fileSize(a.size)}</span>
                </a>
              )
            ))}
          </div>
        )}

        {m.reactions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {m.reactions.map((r) => (
              <button key={r.emoji} onClick={() => react.mutate(r.emoji)} title={r.who.join(', ')}
                className={`text-[11.5px] px-1.5 py-0.5 rounded-full border ${r.mine ? 'border-teal bg-teal-50 text-teal-d' : 'border-line bg-canvas text-muted hover:border-navy-50'}`}>
                {r.emoji} {r.count}
              </button>
            ))}
          </div>
        )}

        {m.replies.length > 0 && (
          <div className="mt-1.5">
            <button onClick={() => setShowReplies((s) => !s)} className="text-[11px] font-semibold text-teal-d hover:underline">
              {showReplies ? 'Hide' : 'Show'} {m.replies.length} {m.replies.length === 1 ? 'reply' : 'replies'}
            </button>
            {showReplies && (
              <div className="mt-1 pl-3 border-l-2 border-line">
                {m.replies.map((r) => (
                  <Message key={r.id} m={r} dealId={dealId} team={team} meName={meName} isReply
                    replyingTo={replyingTo} setReplyingTo={setReplyingTo} editing={editing} setEditing={setEditing} onReply={onReply} />
                ))}
              </div>
            )}
          </div>
        )}

        {replyingTo === m.id && (
          <div className="mt-2 pl-3 border-l-2 border-teal-50">
            <Composer dealId={dealId} team={team} placeholder={`Reply to ${m.author.name}…`} submitLabel="Reply" autoFocus allowAttachments
              onSubmit={(v) => { setReplyingTo(null); onReply(v, m.id); }} onCancel={() => setReplyingTo(null)} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- channel -- */

export function Discussion({ dealId }: { dealId: string }) {
  const qc = useQueryClient();
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [onlyMine, setOnlyMine] = useState(false);
  const [failed, setFailed] = useState<{ draft: Draft; parentId: string | null; error: string } | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const seenCount = useRef(0);
  const tmpSeq = useRef(0);

  const { data: teamData } = useQuery({ queryKey: ['team'], queryFn: getTeam, retry: false, staleTime: 300_000 });
  const { data: meData } = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false, staleTime: 300_000 });
  const { data, isLoading, error } = useQuery({
    queryKey: keyFor(dealId),
    queryFn: () => getDiscussion(dealId) as Promise<Channel>,
    // The channel is collaborative — poll so a teammate's message shows up without a reload.
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });

  const team = useMemo(() => (teamData?.team ?? []).filter((m) => m.active), [teamData]);
  const me = meData?.identity;
  const meName = me?.name ?? '';
  const messages = data?.messages ?? [];

  const post = useMutation({
    mutationFn: (v: { draft: Draft; parentId: string | null }) =>
      postDiscussion(dealId, { ...v.draft, parentId: v.parentId }),
    onMutate: async ({ draft, parentId }) => {
      // Stop an in-flight poll from landing on top of the optimistic write.
      await qc.cancelQueries({ queryKey: keyFor(dealId) });
      const tmpId = `tmp_${++tmpSeq.current}`;
      const mine = team.find((t) => t.id === me?.memberId);
      const optimistic: UiMessage = {
        id: tmpId,
        parentId,
        author: { id: me?.memberId ?? null, name: mine?.name ?? meName, initials: mine?.initials ?? meName.slice(0, 2) },
        body: draft.body,
        createdAt: new Date().toISOString(),
        editedAt: null,
        deleted: false,
        mine: true,
        canEdit: false, // no editing until it has a real id on the server
        mentions: draft.mentions.map((id) => ({ id, name: team.find((t) => t.id === id)?.name ?? '' })),
        reactions: [],
        // Attachments are already uploaded by this point, but the composer holds their metadata;
        // the server's reply fills them in a moment later.
        attachments: [],
        replies: [],
        pending: true,
      };
      setFailed(null);
      return { prev: editCache(qc, dealId, (l) => insertIn(l, optimistic)), tmpId };
    },
    onError: (e, v, ctx) => {
      restoreCache(qc, dealId, ctx?.prev);
      // Keep the text so nothing typed is lost to a failed request.
      setFailed({ draft: v.draft, parentId: v.parentId, error: (e as Error).message });
    },
    onSuccess: (d, _v, ctx) => {
      if (ctx?.tmpId) editCache(qc, dealId, (l) => replaceOrInsert(l, ctx.tmpId, d.message));
    },
  });
  const send = (draft: Draft, parentId: string | null = null) => post.mutate({ draft, parentId });

  // Opening the tab clears my @mention badges for this deal's messages.
  const mentionedIds = useMemo(() => {
    if (!me?.memberId) return [];
    const walk = (list: UiMessage[]): string[] =>
      list.flatMap((m) => [...(m.mentions.some((x) => x.id === me.memberId) ? [m.id] : []), ...walk(m.replies)]);
    return walk(messages).filter((id) => !id.startsWith('tmp_'));
  }, [messages, me]);
  const markRead = useMutation({
    mutationFn: (ids: string[]) => readDiscussionMentions(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markedFor = useRef('');
  useEffect(() => {
    const key = `${dealId}:${mentionedIds.join(',')}`;
    if (!mentionedIds.length || markedFor.current === key) return;
    markedFor.current = key;
    markRead.mutate(mentionedIds);
    // markRead is a stable mutation handle; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, mentionedIds]);

  const totals = useMemo(() => {
    let count = 0;
    const people = new Set<string>();
    const walk = (list: UiMessage[]) => {
      for (const m of list) { if (!m.deleted) { count++; people.add(m.author.name); } walk(m.replies); }
    };
    walk(messages);
    return { count, people: people.size };
  }, [messages]);

  const visible = useMemo(() => {
    if (!onlyMine || !me?.memberId) return messages;
    const touchesMe = (m: UiMessage): boolean =>
      m.author.id === me.memberId || m.mentions.some((x) => x.id === me.memberId) || m.replies.some(touchesMe);
    return messages.filter(touchesMe);
  }, [messages, onlyMine, me]);

  // Jump to the newest message when the channel grows (first load included).
  useEffect(() => {
    if (totals.count !== seenCount.current) {
      seenCount.current = totals.count;
      bottomRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [totals.count]);

  return (
    <Card
      className="mb-4"
      title="Discussion"
      sub={isLoading ? 'Loading…' : `${totals.count} message${totals.count !== 1 ? 's' : ''} · ${totals.people} participant${totals.people !== 1 ? 's' : ''}`}
      actions={
        <button onClick={() => setOnlyMine((v) => !v)}
          className={`text-[11.5px] px-2 py-1 rounded-md border ${onlyMine ? 'border-teal bg-teal-50 text-teal-d font-semibold' : 'border-line text-muted hover:text-navy'}`}>
          Involving me
        </button>
      }
    >
      {error && <div className="mb-3 text-[12px] text-red">Couldn’t load the discussion: {(error as Error).message}</div>}

      <div className="max-h-[60vh] overflow-y-auto pr-1 -mr-1">
        {isLoading ? (
          <div className="py-6 text-center"><Spinner /></div>
        ) : visible.length === 0 ? (
          <div className="text-center py-8 text-muted text-[12.5px]">
            {onlyMine ? 'Nothing here involves you yet.' : 'No discussion yet — start the thread below.'}
          </div>
        ) : (
          visible.map((m, i) => (
            <div key={m.id}>
              {(i === 0 || dayKey(visible[i - 1]!.createdAt) !== dayKey(m.createdAt)) && (
                <div className="flex items-center gap-2 my-2">
                  <span className="h-px flex-1 bg-line" />
                  <span className="text-[10px] uppercase tracking-wide text-muted-2 font-semibold">{dayLabel(m.createdAt)}</span>
                  <span className="h-px flex-1 bg-line" />
                </div>
              )}
              <Message m={m} dealId={dealId} team={team} meName={meName}
                replyingTo={replyingTo} setReplyingTo={setReplyingTo} editing={editing} setEditing={setEditing} onReply={send} />
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {failed && (
        <div className="mt-3 p-2.5 rounded-lg bg-red-50 border border-red/20 text-[12px]">
          <div className="text-red font-semibold">Couldn’t post that message — {failed.error}</div>
          <div className="mt-1 text-ink whitespace-pre-wrap break-words">{failed.draft.body}</div>
          <div className="mt-1.5 flex gap-2">
            <button onClick={() => { const f = failed; setFailed(null); send(f.draft, f.parentId); }}
              className="text-[11.5px] px-2 py-1 rounded-md bg-teal text-white font-semibold hover:bg-teal-d">Retry</button>
            <button onClick={() => setFailed(null)} className="text-[11.5px] px-2 py-1 rounded-md border border-line text-muted hover:text-navy">Discard</button>
          </div>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-line-2">
        <Composer dealId={dealId} team={team} allowAttachments
          placeholder="Share an update, ask a question, tag a teammate with @…" submitLabel="Post"
          onSubmit={(v) => send(v)} />
      </div>
    </Card>
  );
}
